/**
 * Created by Ivo Mehide <ivo@degeetia.ee> on 05.11.17.
 */

import React from "react";
import PropTypes from "prop-types";
import _ from "underscore";

import BaseActionWizardStep from "../../containers/BaseActionWizardStep";
import PasswordForm from "../../components/PasswordForm";
import {SessionStore} from "../../services/Session";
import {t} from "../../trans";
import {PutRestAction} from "../../services/RestAction";
import Navigation from "../../Navigation";

class StepChangePassword extends BaseActionWizardStep {
    static propTypes = {
        /**
         * Password change type:
         *  a) "session" - password change request by session during login
         *  b) "forgot" - password change request by forgot link
         *  c) "password" - password change requested by already authenticated user
         *  d) "authkey" - auth code change requested by already authenticated user
         *  e) "both" - password and auth code change requested by already authenticated user
         */
        type: PropTypes.oneOf(["session", "forgot", "password", "authkey", "both"]).isRequired,
        /**
         * User password
         */
        password: PropTypes.string,
        /**
         * Temporary auth token (in case when we have password reset link)
         */
        authToken: PropTypes.string,
        personId: PropTypes.string,
        /**
         *
         */
        personEmail: PropTypes.string,
        /**
         * OAuth2 client_id
         */
        clientId: PropTypes.string.isRequired,
        /**
         * OAuth2 redirect_url
         */
        redirectUrl: PropTypes.string.isRequired,
        /**
         * OAuth2 state
         */
        nonce: PropTypes.string,
        /**
         * OAuth2 scope
         */
        scope: PropTypes.string,

        onParamChange: PropTypes.func.isRequired,
        params: PropTypes.shape({
            hideForward: PropTypes.bool,
            hideBack: PropTypes.bool,
            disableForward: PropTypes.bool,
            disableBack: PropTypes.bool
        }).isRequired,
        prevParams: PropTypes.shape({
            hideForward: PropTypes.bool,
            hideBack: PropTypes.bool,
            disableForward: PropTypes.bool,
            disableBack: PropTypes.bool,
            componentProps: PropTypes.object,
            authKey: PropTypes.string
        })
    };

    static defaultProps = {
        type: "session"
    };

    constructor(props) {
        super(props, {
            session: SessionStore,
            newSession: "session",
            person: "person"
        });
        this.state.submitted = false;
        this.state.errors = {};
        this.state.password = props.password;
        this.state.newPassword = "";
        this.state.newPassword2 = "";
        this.state.code = "";

        switch (props.type) {
            case "session":
                this.state.personId = this.state.session.currentRecord.person.id;
                this.state.email = this.state.session.currentRecord.person.email;
                break;

            case "forgot":
                this.state.email = "";
                break;

            case "password":
            case "authkey":
            case "both":
                this.state.personId = props.personId;
                this.state.email = props.personEmail;
                break;
        }
        this.onChange = this.onChange.bind(this);
    }

    sessionAuthType() {
        if (this.props.type === "forgot") return "A";
        if (this.askCode()) return "T";
        return "P";
    }

    askCode() {
        switch (this.props.type) {
            case "session":
                let authKey = (this.props.prevParams && this.props.prevParams.authKey ? this.props.prevParams.authKey : null);
                if (authKey) return true;
                let session = this.state.session.currentRecord;
                if (!session || !session.person) return false;
                return (session.person.requirePasswordChange ? (session.person.authKey || session.authType == "T" ? true : false) : true);

            case "forgot":
                if (this.props.authToken && this.props.authToken.length > 0 &&
                    (this.props.authToken.charAt(0) == 'T' || this.props.authToken.charAt(0) == 'X')) return true;
                return false;

            case "authkey":
            case "both":
                return true;

            case "password":
                return false;
        }
        return false;
    }

    askOldCode() {
        if (this.state.session.currentRecord.authType === "T") return true;
        return false;
    }

    askOldPassword() {
        switch (this.props.type) {
            case "session":
                return false;

            case "forgot":
                return false;

            case "password":
            case "authkey":
            case "both":
            default:
                return false;
        }
    }

    askNewPassword() {
        switch (this.props.type) {
            case "session":
                return this.state.session.currentRecord.person.requirePasswordChange;

            case "forgot":
                // if (this.props.authToken && this.props.authToken.length > 0 &&
                //    (this.props.authToken.charAt(0) != 'T')) return true;
                //return false;
                return true;

            case "password":
            case "both":
                return true;

            case "authkey":
            default:
                return false;
        }
    }

    render() {
        let validation = (this.state.submitted ? this.makeErrorList() : null);
        return (
            <PasswordForm
                onChange={this.onChange}
                askCode={this.askCode()}
                lockEmail={true}
                email={this.state.email}
                password={this.state.password}
                newPassword={this.state.newPassword}
                newPassword2={this.state.newPassword2}
                code={this.state.code}
                disabled={this.state.session.isLoading}
                validation={validation}
                noPassword={!this.askOldPassword()}
                noNewPassword={!this.askNewPassword()}
            />
        );
    }

    isValid(state) {
        return this.validate(state) == null;
    }

    validate(state) {
        if (!state) state = this.state;
        let newValidation = {};
        if (state.email == "" && !this.props.authToken) newValidation.email = t("Required");
        if (state.code == "" && this.askCode()) newValidation.code = t("Required");
        if (state.password == "" && this.askOldPassword()) newValidation.password = t("Required");
        if (this.askNewPassword()) {
            if (state.newPassword == "") newValidation.newPassword = t("Required");
            if (state.newPassword2 == "") newValidation.newPassword2 = t("Required");
            if (state.newPassword != "" && state.newPassword2 != "") {
                if (state.newPassword != state.newPassword2) newValidation.newPassword2 = t("New password does not match the confirm password");
            }
        }
        if (_.isEmpty(newValidation)) return null;
        return newValidation;
    }

    makeErrorList() {
        return _.extend({}, this.state.errors, this.validate());
    }

    onChange(field, newValue) {
        this.setState({[field]: newValue});
    }

    /**
     *
     * @param {string} key
     * @param {SessionState} newState
     */
    loadingFinished(key, newState) {
        switch (key) {
            case "session":
                // Password successfully changed:
                if (this.props.authToken) Navigation.gotoLogin();
                else this.navigateForward();
                break;

            case "newSession":
                // Authentication done. Change password:
                this.personChangePassword();
                break;

            case "person":
                // Password successfully changed:
                this.navigateForward();
                break;
        }
    }

    /**
     * Called when specified store fails to load data.
     * By default this method uses notification to display error.
     * @param key Store key
     * @param {BaseRestStoreState} newState Store new state
     * @param {BaseRestStoreState} oldState Store old state
     */
    loadingFailed(key, newState) {
        if (key === "session" || key === "person" || key == "newSession") {
            let errorList = this.getErrorList(newState.action);
            if (!errorList) return super.loadingFailed(key, newState);
            this.setState({errors: errorList});
        } else
            return super.loadingFailed(key, newState);
    }

    onClickForward() {
        this.setState({submitted: true}, function () {
            if (!this.isValid()) {
                return;
            }
            switch (this.props.type) {
                case "session":
                    this.sessionChangePassword();
                    break;

                case "forgot":
                    this.sessionChangePassword();
                    break;

                case "password":
                case "authkey":
                case "both":
                    if (this.askOldPassword()) this.authentication();
                    else this.personChangePassword();
                    break;
            }
        }.bind(this));
    }

    /**
     * Changes password through session service.
     * This is used when session requires password change
     */
    sessionChangePassword() {
        let authKey = (this.props.prevParams && this.props.prevParams.authKey ? this.props.prevParams.authKey : null);
        let authRequest = {
            "person": {
                "email": (this.state.email ? this.state.email : null),
                "password": this.state.password,
                "authCode": (this.askCode() ? this.state.code : null),
                "newPassword": (this.askNewPassword() ? this.state.newPassword : null),
                "authKey": authKey
            },
            "application": {
                "code": this.props.clientId,
                "redirectUrl": this.props.redirectUrl
            },
            "authType": (authKey ? "P" : this.sessionAuthType()),
            "nonce": this.props.nonce
        };
        if (this.props.scope) {
            // Scope is set:
            let scopeInfo = this.scope.split("=", 2);
            authRequest.activeRole = {
                organization: {
                    clientId: scopeInfo[0]
                },
                role: {
                    clientId: scopeInfo[1]
                }
            };
        }
        if (this.props.authToken) {
            authRequest.authToken = this.props.authToken;
            authRequest.status = "R";
        }
        else {
            authRequest.person.password = this.state.password;
        }
        this.actions("session").changePassword(authRequest);
    }

    /**
     * This is used when user decides to change password after authentication.
     * Authenticate person.
     */
    authentication() {
        let request = {
            status: "P",
            authType: (this.askOldCode() ? "T" : "P"),
            person: {
                email: this.state.session.currentRecord.person.email,
                password: this.state.password,
                authCode: (this.askOldCode() ? this.state.code : null)
            }
        };
        // First we will authenticate user and when this succeeds we will do password change:
        this.actions("newSession").post(request);
    }

    personChangePassword() {
        let requiredAuthType = null;
        switch (this.props.type) {
            case "authkey":
            case "both":
                if (this.state.session.currentRecord.person.requiredAuthType === "P")
                    requiredAuthType = "T";
                break;
            default:
                break;
        }

        let request = {
            "requiredAuthType": requiredAuthType,
            "email": (this.state.email ? this.state.email : null),
            "password": this.state.password,
            "authKey": (this.props.prevParams && this.props.prevParams.authKey ? this.props.prevParams.authKey : null),
            "authCode": (this.askCode() ? this.state.code : null),
            "newPassword": (this.askNewPassword() ? this.state.newPassword : null)
        }
        this.actions("person").put.defer(this.state.personId, request);
    }

    personActionPut(personId, request, authToken = null) {
        let action = new PutRestAction(personId, request);
        if (authToken) action.token = authToken;
        this.actions("person").actionStart.defer(action);
    }

}

export default StepChangePassword;
